import { Component } from 'react';
import common from '../styles/common.module.css';
import copyText from '../utils/copyText';
import { diag, errorSummary } from '../diagnostics/log';
import { formatBugReport } from '../diagnostics/report';

/**
 * The thing that catches a panel taking the whole session down with it.
 *
 * IT USED TO SAY NOTHING. "Combat log panel error. Reload" is true and
 * useless: the message, the file and the line all went to `console.error`
 * and nowhere else, so every crash cost a round trip through devtools -
 * and a crash nobody can reproduce is exactly the one where that round
 * trip does not happen and the report arrives as "it broke somewhere
 * near the map" (v8.42.0).
 *
 * So the error is ON SCREEN now, with a Copy button, because the person
 * who hit it is the only one who was there. The copy is the WHOLE bug
 * report (v8.310.0): the stack alone said where the render died and
 * nothing about the twenty seconds before it.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, stack: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
    diag.error('crash', `${this.props.label || 'Panel'} crashed: ${error?.message ?? error}`, {
      ...errorSummary(error),
      componentStack: errorInfo?.componentStack || '',
    });
    // The COMPONENT stack, which names the panel and the tree beneath it -
    // usually more use than the JS stack, since a render crash's JS frames
    // are mostly React's own.
    this.setState({ stack: errorInfo?.componentStack || '' });
  }

  report() {
    const { error, stack } = this.state;
    return [`${this.props.label || 'Panel'} error`, error?.stack || String(error), stack].filter(Boolean).join('\n\n');
  }

  /** The stack, then everything the app recorded before it. Through copyText: a bare clipboard call dies on http. */
  copyReport() {
    const stamp = formatBugReport({
      context: diag.context(),
      entries: diag.entries(),
      env: { userAgent: navigator.userAgent, url: window.location.href, now: Date.now() },
    });
    return copyText(`${this.report()}\n\n${stamp}`);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const { error } = this.state;
    // Named when we know the panel, generic when we do not - never
    // "Something went wrong error".
    const heading = this.props.label ? `${this.props.label} error` : 'Something went wrong';
    return (
      <div style={{ padding: '20px', color: 'var(--text-muted)', overflow: 'auto', maxHeight: '100%' }}>
        <h2 style={{ color: 'var(--danger)', margin: '0 0 8px', fontSize: '15px' }}>{heading}</h2>
        {/* THE MESSAGE, unabbreviated. It is the one line that usually
            identifies the fault on its own. */}
        <p
          data-testid="error-message"
          style={{ margin: '0 0 12px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: '12px' }}
        >
          {error?.message || String(error) || 'An unexpected error occurred.'}
        </p>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button className={common.btnSecondary} onClick={() => this.setState({ hasError: false, error: null })}>
            Try Again
          </button>
          <button className={common.btnSecondary} onClick={() => window.location.reload()}>
            Reload
          </button>
          <button className={common.btnSecondary} data-testid="error-copy" onClick={() => void this.copyReport()}>
            Copy details
          </button>
        </div>
        {/* Folded away: the stack matters to whoever is fixing it and to
            nobody else, and an open wall of frames buries the message. */}
        <details>
          <summary style={{ cursor: 'pointer', fontSize: '11px' }}>Where it happened</summary>
          <pre
            data-testid="error-stack"
            style={{ fontSize: '10px', whiteSpace: 'pre-wrap', color: 'var(--text-dim)', margin: '8px 0 0' }}
          >
            {this.report()}
          </pre>
        </details>
      </div>
    );
  }
}
